/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Box, Tooltip } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import SelectAllIcon from '@material-ui/icons/SelectAll';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import RefreshIcon from '@material-ui/icons/Refresh';
import GroupIcon from '@material-ui/icons/Group';
import CircularProgress from '@material-ui/core/CircularProgress';
import SubjectIcon from '@material-ui/icons/Subject';
import SummaryHourStats from './SummaryHourStats';
import { Filters } from '../Filters/Filters';
import AdvancedFilters from '../Filters/AdvanceFilters/AdvanceFiltersContainer';
import ErrorNotification from '../ErrorNotification/ErrorNotification';
import ClinicianGroupsOnDay from './ClinicianGroups/ClinicianGroupsOnDay';
import AutoAllocatePage from './AutoAllocation/AutoAllocatePage';
import globals from '../../../globals/communityGlobals';
import { parseDateUtc, isTodaysDate } from '../../../utils/dates';
import { checkFeatureAccess } from '../../../utils/helpers';
import _ from '../../../utils/lodash';
import TimelineView from '../timeline/TimelineView';
import ConfirmationDialog from '../../PageStructure/ConfirmationDialog';
import TimelineLegend from '../timeline/TimelineLegend';
import NonBlockingLoader from '../../common/Loading/NonBlockingLoader';
import BlockingLoader from '../../common/Loading/BlockingLoader';
import useTimeline from '../timeline/useTimeline';
import {
  autoUnallocate,
  selectDateAndUpdate,
  setLiveTrackingSetting,
  resetRunningAutoAllocate,
} from '../../../actions/areas/dailyPlanning';
import { autoAllocationStatuses } from '../../../settings/constants';
import { MoreOptionsDialog } from './MoreOptionsDialog';
import AutoAllocateForm from './AutoAllocateForm';
import AddNpaPlanningForm from '../ClinicianView/AddNpaPlanningForm';

const useStyles = makeStyles({
  clinicianPopModal: {
    backgroundColor: '#333333',
    color: '#fff',
    padding: '5px',
    minWidth: '135px',
  },
});

const AUTO_ALLOCATE_STATUS_CHANGE_INTERVAL = 30000;
const autoAllocateText = 'Auto Allocate is in progress for this unit';
const unallocateText = 'Unallocating is in progress for this unit';
const bulkCancelText = 'Bulk Cancel is in progress for this unit';

export default function TimelineScheduler({ settings }) {
  if (settings === null) return null;
  const faList = useSelector(store => store.global.auth.featureAccessList);
  const dispatch = useDispatch();
  const [showMoreAnchor, setShowMoreAnchor] = useState(null);
  const classes = useStyles();
  const {
    getFlag,
    toggleFlag,
    setFlagAndClinician,
    toggleEditMode,
    resetContext,
    startLiveTrackingInterval,
    stopLiveTrackingInterval,
    setAlertMessage,
  } = useTimeline();

  const [componentState, setComponentState] = useState({
    showConfirmationDialog: false,
    display: false,
    showClinicianGroupsOnDayPanel: false,
    showAutoAllocateLogsPanel: false,
    showAutoAllocateForm: false,
    showAddNpaForm: false,
    disableDayNavigationButtons: false,
  });
  const [showAdvanceFiltersState, setShowAdvanceFiltersState] = useState(false);
  const [clinicianTreatmentInstances, setClinicianTreatmentInstances] = useState([]);
  const [isClinicianTreatmentInstancesDataLoading, setIsClinicianTreatmentInstancesDataLoading] = useState(false);
  const [hasInstancesChanged, setHasInstancesChanged] = useState(false);
  const [shouldResetAdvanceFilter, setShouldResetAdvanceFilter] = useState(false);
  const [filteredInstances, setFilteredInstances] = useState({
    isAdvanceFilterActive: false,
    shouldFilterAssignments: false,
    filteredTreatmentInstances: [],
    filteredAssignments: [],
    filteredCancelledTreatmentInstances: [],
    filteredClinicianTreatmentInstances: [],
  });

  const [autoAllocateState, setAutoAllocateState] = useState({ modes: [], groupsToExclude: [] });
  const [autoAllocateIntervalId, setAutoAllocateIntervalId] = useState(null);

  const isNightMode = useSelector(state => state.areas.dailyPlanning.scheduler.isNightMode);
  const isLiveTracking = useSelector(state => state.areas.dailyPlanning.moreOptions.liveTracking);
  const selectedDate = useSelector(state => state.areas.dailyPlanning.selectedDate);
  const orgUnitId = useSelector(state => state.areas.dailyPlanning.filters.selectedTeam);
  const orgUnitTeamId = useSelector(state => state.areas.dailyPlanning.filters.orgUnitTeam);
  const isBulkCancelInProgress = useSelector(state => state.areas.dailyPlanning.moreOptions.isBulkCancelInProgress);
  const canAllocateWork = checkFeatureAccess(faList, globals.applicationFeatureAccesses.canAllocateWork);
  const canAddNpa = checkFeatureAccess(faList, globals.applicationFeatureAccesses.canSetUpTeamMeetings);

  const autoAllocateInProgress = useSelector(state => state.areas.dailyPlanning.autoAllocate.autoAllocateInProgress);
  const autoAllocateByUnit = _.find(
    autoAllocateInProgress,
    item => item.orgUnitId === orgUnitId && item.date === selectedDate,
  );
  const autoAllocateDate = autoAllocateByUnit ? autoAllocateByUnit.date : null;
  const autoAllocateUnit = autoAllocateByUnit ? autoAllocateByUnit.orgUnitId : 0;
  const autoAllocateStatus = autoAllocateByUnit ? autoAllocateByUnit.status : 'Idle';
  const isUnallocating = autoAllocateByUnit ? autoAllocateByUnit.isUnAllocating : false;
  const hasData = settings.dataItems.length > 0;
  const onDate = parseDateUtc(selectedDate);

  const isAutoAllocateTrackingIntervalSet = () => autoAllocateIntervalId !== null;

  const isAutoAllocateStatusNotIdleForSelectedDateAndUnit = () =>
    (autoAllocateStatus !== autoAllocationStatuses.IDLE || isUnallocating) &&
    autoAllocateDate === selectedDate &&
    autoAllocateUnit === orgUnitId;

  const trackAutoAllocateByFetchingPlanningDetails = (startTrackingCondition, stopTrackingCondition) => {
    if (startTrackingCondition) {
      const intervalId = setInterval(() => settings.toggleForceRefresh(), AUTO_ALLOCATE_STATUS_CHANGE_INTERVAL);
      setAutoAllocateIntervalId(intervalId);
    } else if (stopTrackingCondition) {
      clearInterval(autoAllocateIntervalId);
      if (isAutoAllocateStatusNotIdleForSelectedDateAndUnit()) {
        dispatch(resetRunningAutoAllocate({ date: selectedDate, orgUnitId }));
      }
      setAutoAllocateIntervalId(null);
    }
  };

  const isAutoallocateInProgress = hasData && isAutoAllocateStatusNotIdleForSelectedDateAndUnit();

  const autoAllocateInProgressAndIntervalNotSet = () =>
    settings.isAutoallocateInProgress && !isAutoAllocateTrackingIntervalSet();

  const autoAllocateDoneAndIntervalSet = () =>
    !settings.isAutoallocateInProgress && isAutoAllocateTrackingIntervalSet();

  trackAutoAllocateByFetchingPlanningDetails(
    autoAllocateInProgressAndIntervalNotSet(),
    autoAllocateDoneAndIntervalSet(),
  );

  useEffect(() => {
    return () => {
      if (isAutoAllocateTrackingIntervalSet()) {
        clearInterval(autoAllocateIntervalId);
      }
    };
  }, []);

  useEffect(() => {
    if (settings.error) setAlertMessage({ message: settings.error });
  }, [settings.error]);

  useEffect(() => {
    if (
      !settings.isAutoallocateInProgress &&
      isAutoAllocateTrackingIntervalSet() &&
      isAutoAllocateStatusNotIdleForSelectedDateAndUnit()
    ) {
      dispatch(resetRunningAutoAllocate({ date: selectedDate, orgUnitId }));
    }
  }, [settings.isAutoallocateInProgress, autoAllocateStatus]);

  useEffect(() => {
    startLiveTrackingInterval(settings.toggleForceRefresh);
    return () => stopLiveTrackingInterval();
  }, [isLiveTracking, selectedDate]);

  useEffect(() => {
    if (filteredInstances.isAdvanceFilterActive) {
      setHasInstancesChanged(true);
    }
    if (!getFlag('showClinicianTreatments')) {
      setIsClinicianTreatmentInstancesDataLoading(false);
    }
  }, [settings.treatmentInstances, clinicianTreatmentInstances]);

  const onAutoUnallocate = () => setComponentState({ ...componentState, showConfirmationDialog: true });

  const handleAutoUnallocateCancel = () => {
    setComponentState({ ...componentState, showConfirmationDialog: false, showAutoAllocateForm: false });
  };

  const autoAllocateCallback = () => {
    resetContext();
    settings.toggleForceRefresh();
    startLiveTrackingInterval(settings.toggleForceRefresh);
    setComponentState({ ...componentState, showAutoAllocateForm: false, showConfirmationDialog: false });
  };

  const handleAutoUnallocateConfirm = () => {
    stopLiveTrackingInterval();
    dispatch(autoUnallocate({ date: selectedDate, orgUnitId, isNightMode, orgUnitTeamId }))
      .then(autoAllocateCallback)
      .catch(autoAllocateCallback);
    handleAutoUnallocateCancel();
  };

  function handleDayChange(isNextDay) {
    const currentDay = isNextDay ? onDate.add(1, 'days') : onDate.subtract(1, 'days');
    const newSelectedDate = parseDateUtc(currentDay).format(globals.dateFormats.date);
    setComponentState({ ...componentState, disableDayNavigationButtons: true, showAutoAllocateForm: false });

    dispatch(setLiveTrackingSetting(isTodaysDate(newSelectedDate)));
    dispatch(selectDateAndUpdate(newSelectedDate));
    setTimeout(() => {
      setFlagAndClinician('showClinicianTreatments', false, 0, 0, ['enableMultipleSelection']);
      setComponentState({ ...componentState, disableDayNavigationButtons: false });
    }, 500);
  }

  const shouldDisableAutoAllocateUnAllocate = () => {
    if (settings.period === null) return true;
    if (settings.isUpdateTriggered) return true;
    if (parseDateUtc(settings.period.startDate).isSameOrAfter(parseDateUtc(globals.now))) {
      return false;
    }
    return !parseDateUtc(globals.now).isBetween(
      parseDateUtc(settings.period.startDate),
      parseDateUtc(settings.period.endDate),
    );
  };

  const getClinicianTreatmentInstances = (treatmentInstances, IsClinicianTreatmentInstancesDataLoading) => {
    if (!IsClinicianTreatmentInstancesDataLoading) {
      setClinicianTreatmentInstances(treatmentInstances);
    }
    setIsClinicianTreatmentInstancesDataLoading(IsClinicianTreatmentInstancesDataLoading);
  };

  const functionLoadingRender = () => (
    <BlockingLoader>
      {isBulkCancelInProgress ? bulkCancelText : isUnallocating ? unallocateText : autoAllocateText}
    </BlockingLoader>
  );

  const timelineContentRender = () => (
    <div className="clinicianview-content-section">
      <section className="scheduler-content-section">
        <TimelineView
          settings={settings}
          treatmentInstances={settings.treatmentInstances}
          getClinicianTreatmentInstances={getClinicianTreatmentInstances}
          filteredInstances={filteredInstances}
        />
      </section>
      {getFlag('showLegend') && <TimelineLegend />}
    </div>
  );

  const useBlockingLoader = isAutoallocateInProgress || settings.isAutoallocateInProgress || isBulkCancelInProgress;

  const shouldHideClinician = index => settings.dataItems[index].activities.availabilities.length === 0;
  const clinicians = settings.clinicians.filter((item, i) => !shouldHideClinician(i));

  const renderNonBlockingLoader = () => {
    if (
      settings.isUpdateTriggered &&
      (!isAutoAllocateTrackingIntervalSet() ||
        (isAutoAllocateTrackingIntervalSet() && componentState.disableDayNavigationButtons))
    ) {
      return <NonBlockingLoader message={'Refreshing the diary'} />;
    }
    return <React.Fragment />;
  };

  const toggleAddNPAForm = () => {
    setShowMoreAnchor(null);
    setComponentState({ ...componentState, showAddNpaForm: !componentState.showAddNpaForm });
  };

  function toggleAdvancedFilters() {
    setShowAdvanceFiltersState(!showAdvanceFiltersState);
  }

  return (
    <React.Fragment>
      <section className="scheduler-header-section" style={{ width: '100%' }}>
        <Box display="flex" flexWrap="nowrap" justifyContent="space-around" alignItems="center">
          <Box className="day-nav" display="flex" flexGrow={1} width="100%" alignItems="flex-end" p={0}>
            <Box p={0}>
              <Button
                variant="contained"
                size="large"
                color="primary"
                className="calendar-btns"
                disabled={componentState.disableDayNavigationButtons}
                onClick={() => handleDayChange(false)}
              >
                <ChevronLeftIcon />
              </Button>
              <Button
                variant="contained"
                size="large"
                color="primary"
                className="calendar-btns"
                disabled={componentState.disableDayNavigationButtons}
                onClick={() => handleDayChange(true)}
              >
                <ChevronRightIcon />
              </Button>
            </Box>
            <Box px={0.5}>
              <Typography variant="h2" style={{ fontSize: '20px' }}>
                {onDate.format('dddd')}
              </Typography>
              <Typography variant="h6" style={{ fontSize: '12px' }}>
                {onDate.format('MMMM')}
              </Typography>
            </Box>
            <Box p={0}>
              <Typography variant="h1" style={{ fontSize: '34px' }}>
                {onDate.format('Do')}
              </Typography>
            </Box>
          </Box>
          <Box flexGrow={1} p={0} className="summary-hours-wrapper">
            <SummaryHourStats summaryHours={settings.summaryHoursStats} />
          </Box>
          {!useBlockingLoader && (
            <Box p={0} flexGrow={1}>
              <div className="scheduler-actions-section">
                <Box display="flex" flexWrap="nowrap" justifyContent="flex-end" p={0} m={0}>
                  <Box px={1} display="flex" alignItems="flex-start">
                    {canAllocateWork && (
                      <React.Fragment>
                        <div className="timeline-btn">
                          {getFlag('isEditMode') ? (
                            <Tooltip title="Close Edit Mode">
                              <IconButton size="small" aria-label="Edit mode" onClick={toggleEditMode}>
                                <span className="icon-svg asw-icon icon-select-all-no" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Edit Mode">
                              <IconButton size="small" aria-label="Edit mode" onClick={toggleEditMode}>
                                <SelectAllIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </div>
                        <div className="timeline-btn">
                          <Tooltip title="Clinician Groups">
                            <span>
                              <IconButton
                                style={{ marginTop: '1px' }}
                                size="small"
                                disabled={
                                  autoAllocateStatus !== autoAllocationStatuses.IDLE ||
                                  isUnallocating ||
                                  shouldDisableAutoAllocateUnAllocate()
                                }
                                aria-label="ClinicianGroups"
                                onClick={() =>
                                  setComponentState({ ...componentState, showClinicianGroupsOnDayPanel: true })
                                }
                              >
                                <GroupIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </div>
                        <div className="timeline-btn" style={{ width: 'max-content' }}>
                          <Tooltip title="Autoallocate">
                            <span>
                              <IconButton
                                size="small"
                                disabled={
                                  autoAllocateStatus !== autoAllocationStatuses.IDLE ||
                                  isUnallocating ||
                                  shouldDisableAutoAllocateUnAllocate()
                                }
                                aria-label="Autoallocate"
                                onClick={() => setComponentState({ ...componentState, showAutoAllocateForm: true })}
                              >
                                <span className="icon-extend icon-autoroster" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </div>
                        <div className="timeline-btn">
                          <Tooltip title="Unallocate">
                            <span>
                              <IconButton
                                style={{ marginTop: '1px' }}
                                size="small"
                                disabled={
                                  autoAllocateStatus !== autoAllocationStatuses.IDLE ||
                                  isUnallocating ||
                                  shouldDisableAutoAllocateUnAllocate()
                                }
                                aria-label="Unallocate"
                                onClick={onAutoUnallocate}
                              >
                                <span className="icon-extend icon-cancel-autoroster" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </div>
                        <div className="timeline-btn">
                          <Tooltip title="AutoAllocate Logs">
                            <span>
                              <IconButton
                                style={{ marginTop: '1px' }}
                                size="small"
                                aria-label="Unallocate"
                                onClick={() =>
                                  setComponentState({ ...componentState, showAutoAllocateLogsPanel: true })
                                }
                              >
                                <SubjectIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </div>
                      </React.Fragment>
                    )}
                    <div className="timeline-btn">
                      {getFlag('showTravelTime') ? (
                        <Tooltip title="Hide Travel Time">
                          <IconButton
                            size="small"
                            aria-label="Show Travel Time"
                            onClick={() => toggleFlag('showTravelTime')}
                          >
                            <DirectionsCarIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Show Travel Time">
                          <IconButton
                            size="small"
                            aria-label="Hide Travel Time"
                            onClick={() => toggleFlag('showTravelTime')}
                          >
                            <span className="icon-extend icon-svg asw-icon icon-direction-car-no" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                    <div className="timeline-btn">
                      {settings.isUpdateTriggered && <CircularProgress size={20} style={{ marginTop: '5px' }} />}
                      {!settings.isUpdateTriggered && (
                        <Tooltip title="Refresh data">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setShouldResetAdvanceFilter(true);
                              settings.toggleForceRefresh();
                            }}
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>

                    <Tooltip title="More Actions">
                      <IconButton
                        size="small"
                        aria-label="More Actions"
                        onClick={event => setShowMoreAnchor(event.target)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>

                    <ConfirmationDialog
                      isOpen={componentState.showConfirmationDialog}
                      confirmationTitle={'Clear Allocations'}
                      confirmationDesc={'Are you sure you want to clear all allocated items for this day?'}
                      onConfirm={handleAutoUnallocateConfirm}
                      onClose={handleAutoUnallocateCancel}
                    />
                    <MoreOptionsDialog
                      clinicianModal={classes.clinicianPopModal}
                      open={showMoreAnchor !== null}
                      showLiveTracking={isTodaysDate(selectedDate)}
                      showAddNpaForm={toggleAddNPAForm}
                      anchorEl={showMoreAnchor}
                      handleClose={() => setShowMoreAnchor(null)}
                      selectedDate={selectedDate}
                      canAddNpa={canAddNpa}
                    />
                    {componentState.showClinicianGroupsOnDayPanel && (
                      <ClinicianGroupsOnDay
                        isOpen={componentState.showClinicianGroupsOnDayPanel}
                        onClose={() => setComponentState({ ...componentState, showClinicianGroupsOnDayPanel: false })}
                        onSave={groupsToExclude => {
                          setComponentState({ ...componentState, showClinicianGroupsOnDayPanel: false });
                          setAutoAllocateState({ ...autoAllocateState, groupsToExclude });
                        }}
                        excludedGroups={autoAllocateState.groupsToExclude}
                      />
                    )}
                    {componentState.showAutoAllocateLogsPanel && (
                      <AutoAllocatePage
                        onClose={() => setComponentState({ ...componentState, showAutoAllocateLogsPanel: false })}
                      />
                    )}
                    {componentState.showAutoAllocateForm && (
                      <AutoAllocateForm
                        selectedDate={selectedDate}
                        isNightMode={isNightMode}
                        orgUnitId={orgUnitId}
                        orgUnitTeamId={orgUnitTeamId}
                        clinicians={clinicians}
                        autoAllocateCallback={autoAllocateCallback}
                        handleAutoUnallocateCancel={handleAutoUnallocateCancel}
                        groupsToExclude={autoAllocateState.groupsToExclude}
                        onClose={() => setComponentState({ ...componentState, showAutoAllocateForm: false })}
                      />
                    )}
                    {componentState.showAddNpaForm && (
                      <AddNpaPlanningForm
                        orgUnitId={orgUnitId}
                        selectedDate={selectedDate}
                        onClose={() => {
                          setComponentState({ ...componentState, showAddNpaForm: false });
                        }}
                        resetAndReload={() => settings.toggleForceRefresh()}
                      />
                    )}
                  </Box>
                </Box>
              </div>
            </Box>
          )}
        </Box>
      </section>
      <section className="clinicianview-filters-section">
        <Filters bands={settings.clinicianBands} toggleAdvancedFilters={toggleAdvancedFilters} />
        <AdvancedFilters
          selectedDate={selectedDate}
          orgUnitId={orgUnitId}
          isClinicianTreatmentInstancesDataLoading={isClinicianTreatmentInstancesDataLoading}
          showAdvanceFiltersState={showAdvanceFiltersState}
          clinicianTreatmentInstances={clinicianTreatmentInstances}
          treatmentInstances={settings.treatmentInstances}
          dataItems={settings.dataItems}
          setFilteredInstances={setFilteredInstances}
          hasInstancesChanged={hasInstancesChanged}
          setHasInstancesChanged={setHasInstancesChanged}
          shouldResetAdvanceFilter={shouldResetAdvanceFilter}
          setShouldResetAdvanceFilter={setShouldResetAdvanceFilter}
          setShowAdvanceFiltersState={setShowAdvanceFiltersState}
        />
      </section>

      {useBlockingLoader && functionLoadingRender()}
      {timelineContentRender()}

      {renderNonBlockingLoader()}
      <ErrorNotification />
    </React.Fragment>
  );
}
